import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Location,
  matchPath,
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { DirtyDialogBlocker, DiscardChangesPrompt } from "./DirtyDialogBlocker";
import { useRoutedOverlayBase } from "./RoutedOverlayBase";
import { useOverlayClose } from "./useOverlayClose";
import {
  createOverlayEntryState,
  decodeRouteSegment,
  encodeRouteSegment,
  isRouteSafeSegment,
  joinPath,
  wasOpenedInApp,
} from "./routedOverlayUtils";

export type RoutedDialogParams = Record<string, string | undefined>;

export interface UseRoutedDialogOptions {
  /** Path relative to the base, may contain params, e.g. `"opportunity-link/:linkId/unlink"`. */
  path: string;
  /**
   * Absolute pathname the dialog is nested under. Defaults to the `RoutedOverlayBase`
   * context. `null` (or no base at all) keeps the dialog in local state, unrouted.
   */
  basePath?: string | null;
  /** Pathname to return to on close. Defaults to the base. */
  closeTo?: string;
  /** Require an exact match (default). Set false when nested overlays extend this path. */
  end?: boolean;
  /** Prompt before Back discards unsaved changes, and warn on refresh or tab close. */
  isDirty?: boolean;
  /**
   * The dialog only makes sense after an in-app action (e.g. it relies on component
   * state), so a deep link or refresh closes it instead of restoring it.
   */
  transient?: boolean;
  /** Pass false when the state the dialog needs is missing; the dialog closes itself. */
  hasFlowState?: boolean;
}

export interface RoutedDialogControls {
  isOpen: boolean;
  isRouted: boolean;
  params: RoutedDialogParams;
  open: (params?: Record<string, string | number>) => void;
  /**
   * Closes without prompting. Returns false when the close was ignored: after unmount,
   * when this dialog's entry is no longer current (a stale callback, e.g. a request that
   * finishes after Cancel), or while a close from this entry is already pending. Callers
   * that persist draft state on close should do so only when this returns true.
   */
  close: () => boolean;
  /** Render this alongside the dialog; it shows the discard prompt when needed. */
  discardPrompt: React.ReactNode;
}

/** Returns undefined, with a warning, when a param value cannot round-trip through the URL. */
const buildDialogPath = (
  pattern: string,
  params: Record<string, string | number> = {},
): string | undefined => {
  const segments: (string | undefined)[] = [];
  for (const segment of pattern.split("/")) {
    if (segment === "*") {
      continue;
    }
    if (!segment.startsWith(":")) {
      segments.push(segment);
      continue;
    }
    const optional = segment.endsWith("?");
    const name = segment.slice(1, optional ? -1 : undefined);
    const value = params[name];
    if (value === undefined || value === "") {
      if (optional) {
        continue;
      }
      throw new Error(
        `Missing route param "${name}" for routed dialog "${pattern}"`,
      );
    }
    if (!isRouteSafeSegment(value)) {
      console.warn(
        `[useRoutedDialog] Invalid value "${value}" for param "${name}" in "${pattern}"; not opening it.`,
      );
      return undefined;
    }
    segments.push(encodeRouteSegment(value));
  }
  return joinPath(...segments);
};

// Only browser routers write `{ key, idx }` to window.history (the first entry has no
// key, matching location key "default"). With a memory router the window state is
// unrelated, so require the key to match the router location.
const getHistoryIndex = (locationKey: string): number | undefined => {
  const state = window.history.state as { key?: unknown; idx?: unknown } | null;
  const stateKey = state?.key ?? "default";
  return stateKey === locationKey && typeof state?.idx === "number"
    ? state.idx
    : undefined;
};

/**
 * Drives a dialog from the URL so opening it creates a history entry, Back closes it,
 * and refresh or a shared link restores it. Without a base path it falls back to local
 * state so the same component can render outside a routed context.
 */
export const useRoutedDialog = ({
  path,
  basePath,
  closeTo,
  end = true,
  isDirty = false,
  transient = false,
  hasFlowState,
}: UseRoutedDialogOptions): RoutedDialogControls => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const closeOverlay = useOverlayClose();
  const contextBase = useRoutedOverlayBase();

  // open and close can run from callbacks that outlive their render (e.g. after an
  // awaited request), so they check the latest location instead of a captured one.
  const latestLocationRef = useRef(location);
  latestLocationRef.current = location;
  // Ignores repeat calls made before the navigation they follow has rendered.
  const pendingNavigationRef = useRef<{ from: Location; to: string } | undefined>(
    undefined,
  );
  // The router's navigate keeps working after unmount, and the refs above stop
  // tracking the location, so a late open or close could navigate from another page.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const base = basePath === undefined ? contextBase : basePath;
  const isRouted = base !== null && base !== undefined;
  const pattern = isRouted ? joinPath(base, path) : undefined;
  const closePath = isRouted ? joinPath(closeTo ?? base) : undefined;

  const match = useMemo(
    () => (pattern ? matchPath({ path: pattern, end }, location.pathname) : null),
    [pattern, end, location.pathname],
  );
  const matched = !!match;

  const routeParams = useMemo<RoutedDialogParams>(() => {
    if (!match) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(match.params)
        .filter(([key]) => key !== "*")
        .map(([key, value]) => [key, value ? decodeRouteSegment(value) : value]),
    );
  }, [match]);

  const [local, setLocal] = useState<{ open: boolean; params: RoutedDialogParams }>({
    open: false,
    params: {},
  });

  const invalidEntry =
    matched &&
    ((transient && !wasOpenedInApp(location)) || hasFlowState === false);

  const wasOpenRef = useRef(false);
  const openHistoryIndexRef = useRef<number | undefined>(undefined);
  const bypassGuardRef = useRef(false);
  const discardDeltaRef = useRef(1);
  const handledLocationKeyRef = useRef<string | undefined>(undefined);
  const [restoring, setRestoring] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  // Computed during render so the dialog (and its form state) stays mounted while
  // history is restored, as long as the dialog's owner remains mounted.
  const leavingDirty =
    isRouted &&
    !matched &&
    wasOpenRef.current &&
    navigationType === NavigationType.Pop &&
    isDirty &&
    !bypassGuardRef.current;

  useEffect(() => {
    if (!isRouted) {
      return;
    }
    if (matched && !invalidEntry) {
      wasOpenRef.current = true;
      bypassGuardRef.current = false;
      handledLocationKeyRef.current = undefined;
      openHistoryIndexRef.current = getHistoryIndex(location.key);
      setRestoring(false);
      return;
    }
    // Effects can re-run for the same location (e.g. StrictMode); navigate only once.
    if (handledLocationKeyRef.current === location.key) {
      return;
    }
    if (invalidEntry && closePath) {
      handledLocationKeyRef.current = location.key;
      closeOverlay(closePath);
      return;
    }
    if (!wasOpenRef.current) {
      return;
    }
    if (leavingDirty) {
      const currentIndex = getHistoryIndex(location.key);
      const openIndex = openHistoryIndexRef.current;
      const delta =
        currentIndex !== undefined && openIndex !== undefined
          ? openIndex - currentIndex
          : 1;
      if (delta !== 0) {
        handledLocationKeyRef.current = location.key;
        discardDeltaRef.current = delta;
        setRestoring(true);
        setPromptOpen(true);
        navigate(delta);
        return;
      }
    }
    wasOpenRef.current = false;
    bypassGuardRef.current = false;
  }, [isRouted, matched, invalidEntry, location.key]);

  const isOpen = isRouted
    ? (matched && !invalidEntry) || leavingDirty || restoring
    : local.open;

  useEffect(() => {
    if (!isRouted || !isOpen || !isDirty) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isRouted, isOpen, isDirty]);

  const open = useCallback(
    (params?: Record<string, string | number>) => {
      if (unmountedRef.current) {
        return;
      }
      if (!pattern) {
        setLocal({
          open: true,
          params: Object.fromEntries(
            Object.entries(params ?? {}).map(([key, value]) => [key, String(value)]),
          ),
        });
        return;
      }
      const current = latestLocationRef.current;
      const pathname = buildDialogPath(pattern, params);
      const pending = pendingNavigationRef.current;
      if (
        pathname === undefined ||
        current.pathname === pathname ||
        (pending?.from === current && pending.to === pathname)
      ) {
        return;
      }
      pendingNavigationRef.current = { from: current, to: pathname };
      navigate(
        { pathname, search: current.search },
        { state: createOverlayEntryState(current) },
      );
    },
    [pattern, navigate],
  );

  const close = useCallback(() => {
    if (unmountedRef.current) {
      return false;
    }
    if (!isRouted) {
      setLocal({ open: false, params: {} });
      return true;
    }
    const current = latestLocationRef.current;
    // A stale callback must not close a later entry or navigate again after dismissal.
    if (
      !matched ||
      !closePath ||
      current.key !== location.key ||
      pendingNavigationRef.current?.from === current
    ) {
      return false;
    }
    pendingNavigationRef.current = { from: current, to: closePath };
    bypassGuardRef.current = true;
    closeOverlay(closePath);
    return true;
  }, [isRouted, matched, closePath, closeOverlay, location.key]);

  const keepEditing = useCallback(() => setPromptOpen(false), []);

  const discard = useCallback(() => {
    setPromptOpen(false);
    bypassGuardRef.current = true;
    navigate(-discardDeltaRef.current);
  }, [navigate]);

  const discardPrompt = (
    <>
      <DiscardChangesPrompt
        open={promptOpen}
        onKeepEditing={keepEditing}
        onDiscard={discard}
      />
      {pattern && matched && !invalidEntry && isDirty && (
        <DirtyDialogBlocker pattern={pattern} end={end} bypassRef={bypassGuardRef} />
      )}
    </>
  );

  return {
    isOpen,
    isRouted,
    params: isRouted ? routeParams : local.params,
    open,
    close,
    discardPrompt,
  };
};
